import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {
  ConnectRun,
  ConnectRunContext,
  ConnectRunReplaySummary,
  ConnectRunStatus,
} from '../../../services/connect/connect-workflows.service';
import {MaterialModule} from '../../../material.module';

type StepStatusSummary = {
  label: string;
  count: number;
  className: string;
};

@Component({
  selector: 'app-connect-workflow-debug-panel',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './connect-workflow-debug-panel.component.html',
  styleUrls: ['./connect-workflow-debug-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectWorkflowDebugPanelComponent {
  run = input<ConnectRun | null>(null);
  runLoading = input<boolean>(false);
  runError = input<string | null>(null);
  context = input<ConnectRunContext | null>(null);
  contextLoading = input<boolean>(false);
  contextError = input<string | null>(null);
  replay = input<ConnectRunReplaySummary | null>(null);
  replayLoading = input<boolean>(false);
  replayError = input<string | null>(null);

  readonly hasContext = computed(() => {
    const context = this.context();
    return !!context && Object.keys(context).length > 0;
  });

  readonly stepStatusSummary = computed<StepStatusSummary[]>(() => {
    const counts = new Map<string, number>();
    for (const step of this.run()?.steps ?? []) {
      const key = this.normalizeStatus(step.status);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([status, count]) => ({
        label: this.humanizeStatus(status),
        count,
        className: `status-pill--${status}`,
      }));
  });

  readonly replayEventCounts = computed(() => {
    const counts = this.replay()?.event_counts ?? {};
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
  });

  formatTimestamp(value?: string): string {
    if (!value) return '-';
    const time = Date.parse(value);
    return Number.isNaN(time) ? value : new Date(time).toLocaleString();
  }

  formatDuration(value?: number): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '-';
    if (value < 1000) return `${Math.round(value)} ms`;
    const totalSeconds = Math.round(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${totalSeconds} s`;
    return `${minutes}m ${seconds}s`;
  }

  runDuration(): string {
    const run = this.run();
    if (!run) return '-';
    if (typeof run.duration_ms === 'number') return this.formatDuration(run.duration_ms);
    if (!run.started_at || !run.finished_at) return '-';
    const started = Date.parse(run.started_at);
    const finished = Date.parse(run.finished_at);
    if (Number.isNaN(started) || Number.isNaN(finished) || finished < started) return '-';
    return this.formatDuration(finished - started);
  }

  formatJson(value: unknown): string {
    if (typeof value === 'undefined') return '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  humanizeStatus(status?: ConnectRunStatus): string {
    const normalized = this.normalizeStatus(status);
    if (!normalized) return '-';
    return normalized.replace(/_/g, ' ');
  }

  statusClass(status?: ConnectRunStatus): string {
    const normalized = this.normalizeStatus(status);
    return normalized ? `status-pill--${normalized}` : '';
  }

  private normalizeStatus(status?: ConnectRunStatus): string {
    return String(status ?? '').trim().toLowerCase();
  }
}
